import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Percent, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api-client';
import { Badge } from '@/shared/ui/components/badge';
import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useDiscountCodes, useUpdateDiscountCode, type DiscountCode } from './api';
import { discountCodeStatus, type DiscountCodeStatus } from './status';

const STATUS_VARIANT: Record<DiscountCodeStatus, 'default' | 'secondary' | 'outline'> = {
  live: 'default',
  scheduled: 'outline',
  expired: 'secondary',
  exhausted: 'secondary',
  off: 'secondary',
};

function discountValueLabel(code: DiscountCode): string {
  if (code.percentageBasisPoints != null) return `${code.percentageBasisPoints / 100}%`;
  if (code.fixedAmount != null)
    return `${code.fixedAmount} ${code.fixedAmountCurrency ?? ''}`.trim();
  return '—';
}

export function DiscountsPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const canManage = useAuthorization().can('discount.manage');

  const { data, isLoading, isError } = useDiscountCodes();

  if (!canManage) return <Forbidden />;

  const items = data?.items ?? [];

  return (
    <section className="grid gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Percent className="size-6" aria-hidden="true" />
          {t('discounts.title')}
        </h1>
        <Button onClick={() => navigate('/admin/discounts/new')}>
          <Plus className="size-4" aria-hidden="true" />
          {t('discounts.actions.create')}
        </Button>
      </div>

      {isLoading && <p className="text-muted-foreground">{t('discounts.loading')}</p>}
      {isError && <p className="text-destructive">{t('discounts.errors.loadFailed')}</p>}

      {data && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('discounts.columns.code')}</th>
                <th className="px-4 py-2 font-medium">{t('discounts.columns.value')}</th>
                <th className="px-4 py-2 font-medium">{t('discounts.columns.scope')}</th>
                <th className="px-4 py-2 font-medium">{t('discounts.columns.status')}</th>
                <th className="px-4 py-2 font-medium">{t('discounts.columns.redemptions')}</th>
                <th className="px-4 py-2 text-right font-medium">
                  {t('discounts.columns.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    {t('discounts.empty')}
                  </td>
                </tr>
              )}
              {items.map((code) => (
                <DiscountCodeRow key={code.id} discountCode={code} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function DiscountCodeRow({ discountCode }: { discountCode: DiscountCode }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const status = discountCodeStatus(discountCode);
  const updateDiscountCode = useUpdateDiscountCode();
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);

  async function toggleActive() {
    try {
      await updateDiscountCode.mutateAsync({
        id: discountCode.id,
        input: {
          code: discountCode.code,
          scope: discountCode.scope,
          percentageBasisPoints: discountCode.percentageBasisPoints,
          fixedAmount: discountCode.fixedAmount,
          fixedAmountCurrency: discountCode.fixedAmountCurrency,
          venueScope: discountCode.venueScope,
          venueId: discountCode.venueId,
          discoverable: discountCode.discoverable,
          allowedSellableIds: discountCode.allowedSellableIds,
          minimumSpendAmount:
            discountCode.minimumSpendAmount != null
              ? Number(discountCode.minimumSpendAmount)
              : undefined,
          startsAt: discountCode.startsAt,
          endsAt: discountCode.endsAt,
          validDaysOfWeek: discountCode.validDaysOfWeek,
          validFromTime: discountCode.validFromTime,
          validUntilTime: discountCode.validUntilTime,
          redemptionCap: discountCode.redemptionCap,
          active: !discountCode.active,
        },
      });
      toast.success(
        t(discountCode.active ? 'discounts.toast.deactivated' : 'discounts.toast.reactivated'),
      );
    } catch (caught) {
      const code = caught instanceof ApiError ? caught.code : undefined;
      const key = code ? `discounts.errors.${code}` : 'discounts.errors.generic';
      const translated = t(key);
      toast.error(translated === key ? t('discounts.errors.generic') : translated);
    }
  }

  const redemptions =
    discountCode.redemptionCap != null
      ? `${discountCode.redeemedUnits} / ${discountCode.redemptionCap}`
      : `${discountCode.redeemedUnits}`;

  return (
    <tr className="border-b last:border-0">
      <td className="px-4 py-2 font-mono font-medium">{discountCode.code}</td>
      <td className="px-4 py-2">{discountValueLabel(discountCode)}</td>
      <td className="px-4 py-2">{t(`discounts.scope.${discountCode.scope}`)}</td>
      <td className="px-4 py-2">
        <Badge variant={STATUS_VARIANT[status]}>{t(`discounts.status.${status}`)}</Badge>
      </td>
      <td className="px-4 py-2 text-muted-foreground">{redemptions}</td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/admin/discounts/${discountCode.id}/edit`)}
          >
            {t('discounts.actions.edit')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={updateDiscountCode.isPending}
            onClick={() => {
              if (discountCode.active) {
                setConfirmingDeactivate(true);
              } else {
                void toggleActive();
              }
            }}
          >
            {discountCode.active
              ? t('discounts.actions.deactivate')
              : t('discounts.actions.reactivate')}
          </Button>
        </div>
      </td>

      <Dialog open={confirmingDeactivate} onOpenChange={setConfirmingDeactivate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('discounts.confirm.deactivateTitle')}</DialogTitle>
            <DialogDescription>
              {t('discounts.confirm.deactivateDescription', { code: discountCode.code })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmingDeactivate(false)}>
              {t('discounts.actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={updateDiscountCode.isPending}
              onClick={() => {
                setConfirmingDeactivate(false);
                void toggleActive();
              }}
            >
              {t('discounts.actions.deactivate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </tr>
  );
}
